import type { ApiResult } from '../types';
import type { NominationWizardDraft } from '../types/nominationCaseRequest';
import type { NominationStepsResponse } from '../types/nominationApi';
import type { DocumentChecklistItem } from '../mocks/criminalMasterData';
import {
  isNominationCategory,
  setNominationCategoryId,
  type NominationWizardStep,
} from '../pages/wizard/nomination/nominationConstants';
import { DEFAULT_NOMINATION_DRAFT } from '../layouts/NominationRequestLayout';
import { caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import {
  buildSaveNominationPayload,
  caseRequestIdFromResponse,
  mapNominationStepsToDraft,
  referenceNoFromResponse,
  resolveNominationDraftResumeStep,
} from './nominationCaseMapper';

export const getFileCategories = criminalService.getFileCategories;

/** Resolve Nomination category id from master data when available. */
export async function resolveNominationCategoryId(): Promise<string> {
  const res = await getFileCategories();
  if (!res.success) return '';
  const match = res.data.find((c) => isNominationCategory(c));
  if (match?.id) setNominationCategoryId(match.id);
  return match?.id ?? '';
}

export const getSubFileCategories = criminalService.getSubFileCategories;
export const getSubCategoryApplicableAreas = criminalService.getSubCategoryApplicableAreas;
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const getDeliveryMethods = criminalService.getDeliveryMethods;
export const uploadCaseFile = criminalService.uploadCaseFile;
export const downloadCaseFile = criminalService.downloadCaseFile;
export {
  getCommonCoverNote as getNominationCoverNote,
  downloadCommonCoverNotePdf as downloadNominationCoverNotePdf,
} from './coverNoteRequests';

export async function getDocumentChecklist(
  fileCategoryId: string,
  subFileCategoryId: string,
): Promise<ApiResult<DocumentChecklistItem[]>> {
  if (!fileCategoryId) return { success: true, data: [] };
  return criminalService.getDocumentChecklist(fileCategoryId, subFileCategoryId);
}

export type SaveNominationResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS-NOM-1 — save/update nomination steps 1–4 */
export async function saveNominationRequest(
  draft: NominationWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: NominationWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveNominationResult>> {
  const body = buildSaveNominationPayload(draft, {
    isDraft: options.isDraft,
    currentStep: options.currentStep,
    allSteps: options.allSteps,
    confirmedCorrect: options.confirmedCorrect,
    isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
  });

  const res = await caseApiRequest<NominationStepsResponse>('/nomination/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: referenceNoFromResponse(res.data),
    },
  };
}

export type NominationRequestViewData = {
  draft: NominationWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: NominationStepsResponse['step_1'];
  step2?: NominationStepsResponse['step_2'];
  step3?: NominationStepsResponse['step_3'];
  step4?: NominationStepsResponse['step_4'];
};

/** CMS-NOM-2 — full GET payload (wizard resume + view) */
export async function getNominationRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<NominationRequestViewData | null>> {
  if (!caseRequestId.trim()) return { success: true, data: null };

  const res = await caseApiRequest<NominationStepsResponse>(
    caseRequestStepsPath(`/nomination/steps/${encodeURIComponent(caseRequestId)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const draft = mapNominationStepsToDraft(res.data, {
    ...DEFAULT_NOMINATION_DRAFT,
    draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
  });

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const submittedAt = (res.data.created_at ?? res.data.updated_at ?? res.data.updatedAt ?? '').trim();
  const raw = res.data as { completion_remarks?: string };

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
    },
  };
}

/** CMS-NOM-2 — load saved nomination steps for continue draft / wizard resume */
export async function getNominationRequestDetails(
  caseRequestId: string,
): Promise<ApiResult<NominationWizardDraft | null>> {
  const view = await getNominationRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

/** CMS-NOM-2 — which wizard step to open when continuing a saved draft */
export async function getNominationDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: NominationWizardStep; requestId: string }>> {
  if (!caseRequestId.trim()) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<NominationStepsResponse>(
    `/nomination/steps/${encodeURIComponent(caseRequestId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || caseRequestId;
  return {
    success: true,
    data: { step: resolveNominationDraftResumeStep(res.data), requestId },
  };
}

export async function submitNominationRequest(
  draft: NominationWizardDraft,
): Promise<ApiResult<SaveNominationResult>> {
  return saveNominationRequest(draft, {
    isDraft: false,
    allSteps: true,
    confirmedCorrect: draft.confirmCorrect,
  });
}

export type { DocumentChecklistItem };
